/**
 * OTP Verification Component
 * 
 * This component handles OTP (One-Time Password) verification for company registration.
 * It integrates with the company signup flow and follows the project's design patterns.
 * 
 * Integration with Backend:
 * 1. Uncomment the otpService.verifyOTP() call in verifyOTP method
 * 2. Uncomment the otpService.resendOTP() call in resendOTP method
 * 3. Modify the signup process to send OTP after successful registration
 * 4. Update the backend endpoints in src/services/otpService.js
 * 
 * Features:
 * - 6-digit OTP input with auto-submit
 * - Resend functionality with countdown timer
 * - Responsive design following project patterns
 * - RTL support for Arabic interface
 * - Header and Footer integration
 */

import React from "react";
import { Layout, notification, Spin } from "antd";
import OTPInput from "react-otp-input";
import Header from "../../../Header";
import Footer from "../../../Footer";
import SEO from "../../../SEO";
import { loadState, saveState } from "../../../../_core/localStorage";
import { connect } from "react-redux";
import otpService from "../../../../services/otpService";
import baseRequest from "../../../../_core";
import "./style.scss";

class OTPVerification extends React.Component {
  state = {
    otp: "",
    email: "",
    resendDisabled: false,
    countdown: 0,
    loading: false,
  };

  componentDidMount() {
    window.scrollTo(0, 0);
    // Get email from localStorage or props
    const state = loadState();
    const email = state?.email || this.props.location?.state?.email;
    
    if (!email) {
      // If no email found, redirect to signup
      this.props.history.push("/company/signup");
      return;
    }
    
    this.setState({ email });
    this.startResendCountdown();
  }

  startResendCountdown = () => {
    this.setState({ resendDisabled: true, countdown: 60 });
    
    const timer = setInterval(() => {
      this.setState((prevState) => {
        if (prevState.countdown <= 1) {
          clearInterval(timer);
          return { resendDisabled: false, countdown: 0 };
        }
        return { countdown: prevState.countdown - 1 };
      });
    }, 1000);
  };

  handleOTPChange = (otp) => {
    this.setState({ otp });
    
    // Auto-submit when OTP is complete (6 digits)
    if (otp.length === 6) {
      setTimeout(() => this.verifyOTP(), 500);
    }
  };

  verifyOTP = async () => {
    const { otp, email } = this.state;
    
    if (!otp || otp.length !== 6) {
      notification.error({
        message: "يرجى إدخال رمز التحقق المكون من 6 أرقام",
      });
      return;
    }

    this.setState({ loading: true });

    try {
      // Use the company OTP verification service
      const response = await otpService.verifyCompanyOTP({ email, otp, isCompany: true });
      
      // Update authentication state after successful OTP verification
      const currentState = loadState();
      const updatedState = {
        ...currentState,
        loggedIn: true,
        role: 'company',
        token: response.token || currentState.token,
        isConfirmed: true
      };
      
      // Save to localStorage
      saveState(updatedState);
      
      // Set authorization header
      if (response.token || currentState.token) {
        baseRequest.addHeader();
      }
      
      // Dispatch Redux action to update the store
      this.props.dispatch({
        type: 'COMPANY_OTP_VERIFIED',
        payload: {
          loggedIn: true,
          role: 'company',
          token: response.token || currentState.token,
          isConfirmed: true
        }
      });
      
      notification.success({
        message: "تم تأكيد الحساب بنجاح",
        description: "مرحباً بك في منصة PTway",
      });

      // Redirect to company home immediately
      this.props.history.push("/company/home");

    } catch (error) {
      console.log(error?.response?.data || error);
      notification.error({
        message: typeof error?.response?.data === "string"
          ? error?.response?.data
          : "رمز التحقق غير صحيح",
      });
      this.setState({ otp: "" });
    } finally {
      this.setState({ loading: false });
    }
  };

  resendOTP = async () => {
    const { email } = this.state;
    
    try {
      // Use the company OTP resend service
      await otpService.resendCompanyOTP({ email, isCompany: true });
      
      notification.success({
        message: "تم إعادة إرسال رمز التحقق بنجاح",
        description: "يرجى فحص بريدك الإلكتروني",
      });
      
      this.startResendCountdown();
      
    } catch (error) {
      console.log(error?.response?.data || error);
      notification.error({
        message: typeof error?.response?.data === "string"
          ? error?.response?.data
          : "حدث خطأ في إعادة الإرسال",
      });
    }
  };

  render() {
    const { otp, email, resendDisabled, countdown, loading } = this.state;

    return (
      <React.Fragment>
        <SEO
          title="تأكيد الحساب"
          description="تأكيد حسابك في منصة PTway للوظائف الجزئية"
        />
        <Header />
        <Layout style={{ background: "#f3f3f3", minHeight: "100vh" }}>
          <div className="otp-verification-container">
            <div className="otp-verification-form">
              <div className="otp-content">
                <div className="otp-header">
                  <h2>تأكيد الحساب</h2>
                  <p>
                    تم إرسال رمز التحقق إلى بريدك الإلكتروني
                    <br />
                    <strong>{email}</strong>
                  </p>
                </div>

                <div className="otp-input-wrapper">
                  <div className="otp-input-container">
                    <OTPInput
                      value={otp}
                      onChange={this.handleOTPChange}
                      numInputs={6}
                      separator={<span style={{ width: "8px" }}></span>}
                      inputStyle={{
                        width: "45px",
                        height: "50px",
                        margin: "0 4px",
                        fontSize: "18px",
                        borderRadius: "6px",
                        border: "1px solid #d9d9d9",
                        textAlign: "center",
                        outline: "none",
                        opacity: loading ? 0.6 : 1,
                      }}
                      focusStyle={{
                        border: "2px solid #3598d4",
                      }}
                      shouldAutoFocus
                      isDisabled={loading}
                    />
                  </div>

                  {loading && (
                    <div className="otp-loading">
                      <Spin size="small" />
                      <span style={{ marginLeft: "8px", color: "#666" }}>
                        جاري التحقق من الرمز...
                      </span>
                    </div>
                  )}

                  <div className="otp-actions">
                    <button
                      className="verify-btn"
                      onClick={this.verifyOTP}
                      disabled={otp.length !== 6 || loading}
                    >
                      {loading ? (
                        <>
                          <Spin size="small" style={{ marginRight: "8px" }} />
                          جاري التحقق...
                        </>
                      ) : (
                        "تأكيد الحساب"
                      )}
                    </button>

                    <div className="resend-section">
                      <span>لم تتلق الرمز؟</span>
                      <button
                        className="resend-btn"
                        onClick={this.resendOTP}
                        disabled={resendDisabled || loading}
                      >
                        {resendDisabled
                          ? `إعادة إرسال خلال ${countdown}ث`
                          : "إعادة إرسال"}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="otp-footer">
                  <p>
                    في حالة عدم وصول الرمز، يرجى التحقق من مجلد الرسائل غير المرغوب فيها
                  </p>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </Layout>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    user: state.user,
  };
};

export default connect(mapStateToProps)(OTPVerification); 